import { Refresh } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { t } from "i18next"
import { FC, useEffect, useState } from "react"
import { Sst2Locker } from "shared/sstv2Types"
import { useAppSelector } from "src/app/hooks"
import {
  useGetSstLockersQuery,
  useGetSstv2LockersQuery,
} from "src/features/partners/partnersApi"
import {
  selectSstLockers,
  SstLockerThing,
} from "src/features/partners/sstThingsSlice"
import { SstLockerDisplay } from "./SstLockerDisplay"

interface SaLockerSelectorDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (locker: SstLockerThing) => void
  sstApiVersion?: "v1" | "v2"
}
const SST_LOCKER_LIMIT = 60

const SaLockerSelectorDialog: FC<SaLockerSelectorDialogProps> = ({
  open,
  onClose,
  onSelect,
  sstApiVersion = "v1",
}) => {
  const lockersFromStore = useAppSelector(
    selectSstLockers,
  ) as unknown as SstLockerThing[]
  const [pageNumber, setPageNumber] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")

  // V1 query
  const v1Query = useGetSstLockersQuery(
    { params: { view: "list" } },
    { skip: sstApiVersion !== "v1" },
  )

  // V2 query
  const v2Query = useGetSstv2LockersQuery(
    { page: 1, count: 100 },
    { skip: sstApiVersion !== "v2" },
  )

  const isLoading =
    sstApiVersion === "v1"
      ? v1Query.isLoading || v1Query.isFetching
      : v2Query.isLoading || v2Query.isFetching
  const isLoadingMore = isLoading

  const refetch = sstApiVersion === "v1" ? v1Query.refetch : v2Query.refetch
  const data = v1Query.data

  // Merge lockers from both sources
  const lockers: SstLockerThing[] =
    sstApiVersion === "v1"
      ? lockersFromStore
      : ((v2Query.data?.data || []) as Sst2Locker[]).map((l) => ({
          _id: l.id,
          client_name: l.name || "",
          client_uuid: l.uuid || "",
          serial_number: l.serial_number || "",
          address_1: l.address || "",
          city: l.city || "",
          province: l.region || "",
          country: l.country || "",
          postal_code: l.postal_code || "",
          latitude: l.latitude,
          longitude: l.longitude,
          is_online: l.is_online,
          nbDoors: l.doors?.length || 0,
          description: l.description || "",
          sstType: "v2",
        } as SstLockerThing))

  const total =
    sstApiVersion === "v1"
      ? data?.data?.total
        ? parseInt(data?.data?.total + "")
        : 0
      : lockers.length
  const hasMore = sstApiVersion === "v1" && total > lockers.length

  const filteredLockers = lockers.filter((locker) => {
    const query = searchQuery.toLowerCase()
    return (
      locker._id.toLowerCase().includes(query) ||
      locker.client_name?.toLowerCase().includes(query) ||
      locker.client_uuid?.toLowerCase().includes(query) ||
      locker.address_1?.toLowerCase().includes(query) ||
      locker.address_2?.toLowerCase().includes(query) ||
      locker.city?.toLowerCase().includes(query) ||
      locker.province?.toLowerCase().includes(query) ||
      locker.country?.toLowerCase().includes(query)
    )
  })

  const handleLoadMore = () => {
    setPageNumber((prev) => prev + 1)
  }

  // refetch if open
  useEffect(() => {
    if (open) {
      refetch()
    }
  }, [open])

  return (
    <Dialog data-ui="UI-SL2W-145" open={open} onClose={onClose}>
      <DialogTitle>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">{`${t("selectLocker")} (${filteredLockers.length} / ${total}) — SST ${sstApiVersion.toUpperCase()}`}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {isLoadingMore && <CircularProgress size={24} />}
            <IconButton onClick={refetch} disabled={isLoadingMore} size="small">
              <Refresh />
            </IconButton>
          </Box>
        </Stack>
        <Divider />
      </DialogTitle>
      <DialogContent
        sx={{
          py: 1,
          my: 1,
          maxHeight: "700px",
          minHeight: "400px",
          overflowY: "auto",
        }}
      >
        <Stack direction="column" spacing={2}>
          <Box pt={1} />
          <TextField
            // label={t("search")}
            variant="outlined"
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by ID, description, client name, address, etc."
          />
          {filteredLockers.map((locker) => (
            <Box
              key={locker._id}
              onClick={() => onSelect(locker)}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                },
              }}
            >
              <SstLockerDisplay locker={locker} />
            </Box>
          ))}
          {hasMore && (
            <Stack direction="column" spacing={1} alignItems="center">
              <Button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                variant="outlined"
                sx={{
                  width: "200px",
                  marginTop: "16px",
                }}
              >
                {t("loadMore")}
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaLockerSelectorDialog
